import { Component, createSignal, Show } from "solid-js";
import { useScenarioStore } from "../stores/scenario";

const SettingsPanel: Component = () => {
	// Get scenario store
	const [scenario, scenarioActions] = useScenarioStore();
	
	// Local state for inputs
	const [name, setName] = createSignal(scenario.meta.name);
	const [startHour, setStartHour] = createSignal(scenario.meta.startTime.hour);
	const [startMinute, setStartMinute] = createSignal(scenario.meta.startTime.minute);
	const [startSecond, setStartSecond] = createSignal(scenario.meta.startTime.second);
	const [crimeHour, setCrimeHour] = createSignal(scenario.meta.crimeTime.hour);
	const [crimeMinute, setCrimeMinute] = createSignal(scenario.meta.crimeTime.minute);
	const [crimeSecond, setCrimeSecond] = createSignal(scenario.meta.crimeTime.second);
	const [endHour, setEndHour] = createSignal(scenario.meta.endTime?.hour ?? 23);
	const [endMinute, setEndMinute] = createSignal(scenario.meta.endTime?.minute ?? 59);
	const [endSecond, setEndSecond] = createSignal(scenario.meta.endTime?.second ?? 59);
	const [hasEndTime, setHasEndTime] = createSignal(!!scenario.meta.endTime);
	const [showAnalysisAtEnd, setShowAnalysisAtEnd] = createSignal(scenario.meta.showAnalysisAtEnd ?? false);
	const [soundEffects, setSoundEffects] = createSignal(scenario.meta.soundEffects);

	const handleSave = () => {
		scenarioActions.updateMeta({
			name: name(),
			startTime: {
				hour: startHour(),
				minute: startMinute(),
				second: startSecond(),
			},
			crimeTime: {
				hour: crimeHour(),
				minute: crimeMinute(),
				second: crimeSecond(),
			},
			endTime: hasEndTime() ? {
				hour: endHour(),
				minute: endMinute(),
				second: endSecond(),
			} : undefined,
			showAnalysisAtEnd: hasEndTime() ? showAnalysisAtEnd() : undefined,
			soundEffects: soundEffects(),
		});
		scenarioActions.setAnalysisScenarioName(name());
		scenarioActions.scheduleSave();
	};

	return (
		<div class="flex-1 overflow-y-auto p-4">
			<h3 class="text-sm font-bold text-gray-700 mb-4">Scenarioinställningar</h3>

			<div class="space-y-4">
				{/* Scenario Name */}
				<div>
					<label class="block text-xs font-medium text-gray-700 mb-1">
						Scenarionamn <span class="text-gray-400">(max 16 tecken)</span>
					</label>
					<input
						type="text"
						value={name()}
						onInput={(e) => setName(e.currentTarget.value.slice(0, 16))}
						onBlur={handleSave}
						placeholder="Mitt scenario"
						maxLength={16}
						class="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>

				{/* Clock Start Time */}
				<div>
					<label class="block text-xs font-medium text-gray-700 mb-1">
						Klockans starttid
					</label>
					<div class="flex items-center gap-2">
						<input
							type="number"
							min="0"
							max="23"
							value={startHour()}
							onInput={(e) => setStartHour(parseInt(e.currentTarget.value) || 0)}
							onBlur={handleSave}
							class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
							placeholder="HH"
						/>
						<span class="text-gray-500">:</span>
						<input
							type="number"
							min="0"
							max="59"
							value={startMinute()}
							onInput={(e) => setStartMinute(parseInt(e.currentTarget.value) || 0)}
							onBlur={handleSave}
							class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
							placeholder="MM"
						/>
						<span class="text-gray-500">:</span>
						<input
							type="number"
							min="0"
							max="59"
							value={startSecond()}
							onInput={(e) => setStartSecond(parseInt(e.currentTarget.value) || 0)}
							onBlur={handleSave}
							class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
							placeholder="SS"
						/>
					</div>
				</div>

				{/* Crime Time */}
				<div>
					<label class="block text-xs font-medium text-gray-700 mb-1">
						Tid för brottet
					</label>
					<div class="flex items-center gap-2">
						<input
							type="number"
							min="0"
							max="23"
							value={crimeHour()}
							onInput={(e) => setCrimeHour(parseInt(e.currentTarget.value) || 0)}
							onBlur={handleSave}
							class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
							placeholder="HH"
						/>
						<span class="text-gray-500">:</span>
						<input
							type="number"
							min="0"
							max="59"
							value={crimeMinute()}
							onInput={(e) => setCrimeMinute(parseInt(e.currentTarget.value) || 0)}
							onBlur={handleSave}
							class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
							placeholder="MM"
						/>
						<span class="text-gray-500">:</span>
						<input
							type="number"
							min="0"
							max="59"
							value={crimeSecond()}
							onInput={(e) => setCrimeSecond(parseInt(e.currentTarget.value) || 0)}
							onBlur={handleSave}
							class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
							placeholder="SS"
						/>
					</div>
				</div>

				{/* End Time (Optional) */}
				<div>
					<label class="flex items-center gap-2 cursor-pointer mb-2">
						<input
							type="checkbox"
							checked={hasEndTime()}
							onChange={(e) => {
								setHasEndTime(e.currentTarget.checked);
								handleSave();
							}}
							class="w-4 h-4 accent-blue-600"
						/>
						<span class="text-xs font-medium text-gray-700">Sluttid för simulering</span>
					</label>
					<Show when={hasEndTime()}>
						<div class="flex items-center gap-2 ml-6">
							<input
								type="number"
								min="0"
								max="23"
								value={endHour()}
								onInput={(e) => setEndHour(parseInt(e.currentTarget.value) || 0)}
								onBlur={handleSave}
								class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
								placeholder="HH"
							/>
							<span class="text-gray-500">:</span>
							<input
								type="number"
								min="0"
								max="59"
								value={endMinute()}
								onInput={(e) => setEndMinute(parseInt(e.currentTarget.value) || 0)}
								onBlur={handleSave}
								class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
								placeholder="MM"
							/>
							<span class="text-gray-500">:</span>
							<input
								type="number"
								min="0"
								max="59"
								value={endSecond()}
								onInput={(e) => setEndSecond(parseInt(e.currentTarget.value) || 0)}
								onBlur={handleSave}
								class="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm text-gray-900 bg-white"
								placeholder="SS"
							/>
						</div>
						<label class="flex items-center gap-2 cursor-pointer mt-2 ml-6">
							<input
								type="checkbox"
								checked={showAnalysisAtEnd()}
								onChange={(e) => {
									setShowAnalysisAtEnd(e.currentTarget.checked);
									handleSave();
								}}
								class="w-4 h-4 accent-blue-600"
							/>
							<span class="text-xs text-gray-700">Visa analys vid slut</span>
						</label>
					</Show>
				</div>

				{/* Sound Effects */}
				<div>
					<label class="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={soundEffects()}
							onChange={(e) => {
								setSoundEffects(e.currentTarget.checked);
								handleSave();
							}}
							class="w-4 h-4 accent-blue-600"
						/>
						<span class="text-sm text-gray-700">Ljudeffekter</span>
					</label>
				</div>

				{/* Info text */}
				<div class="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
					<p class="text-xs text-blue-900">
						<span class="font-semibold">Tips:</span> Ändringar sparas automatiskt när du lämnar ett fält.
					</p>
				</div>
			</div>
		</div>
	);
};

export default SettingsPanel;
